/**
 * Class description
 *
 * Created: 2019-08-13
 * @author jonas
 */

import {ParsedGameData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedGameData";
import {WinterHoardGameModuleConfig} from "../WinterHoardGameModuleConfig";
import {ParsedServerData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedServerData";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {IBetLineWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {Timeline, TimelineLite} from "gsap";
import {GameRoundPresentation} from "@nolimitcity/slot-game/bin/timelinegameround/core/gameround/GameRoundPresentation";
import {SymbolUtil} from "../utils/SymbolUtil";
import {StateMatrix} from "../utils/StateMatrix";
import {ISymbolStateConfig, StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {WinterHoardParsedGameData} from "../server/data/IWinterHoardServerData";
import { Reels } from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {GameMode} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {BetLineEvent} from "@nolimitcity/slot-game/bin/core/betline/event/BetLineEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {SoundPlayer} from "../sound/SoundPlayer";
import {WinterHoardSoundConfig} from "../WinterHoardSoundConfig";
import {WinterHoardGameConfig} from "../WinterHoardGameConfig";
import {WinterHoard} from "../WinterHoard";


export class InitialWin implements GameRoundPresentation {
    private _gameData:ParsedGameData;
    private _symbolStates:StateMatrix<string>;
    private _tl:TimelineLite;

    constructor() {
    }


    public prepare(data:ParsedServerData):void {
        if(data instanceof WinterHoardParsedGameData) {
            WinterHoardGameModuleConfig.countUpView.calculateTextScale(0, data.singleWin);
             // #287
            WinterHoardGameConfig.instance.WIN_BELOW_STAKE = (WinterHoard.api.gameClientConfiguration.belowStakeWinRestriction && (data.singleWin <= data.playedBetValue));
        }
        this._gameData = <ParsedGameData> data;
        this._symbolStates = SymbolUtil.createSymbolStateMatrixFromBetWins(
            <IBetLineWinData[]>this._gameData.betWins!,
            GameConfig.instance.SYMBOL_STATES.noWin!.keyword,
            GameConfig.instance.SYMBOL_STATES.win!.keyword);
    }

    public abort(isActive:boolean):TimelineLite | null {
        if(isActive && this._tl) {
            //GameRoundManager.detachAndPlayTimeline(this._tl);
            //return this._tl;
            EventHandler.dispatchEvent(new GameEvent(BetLineEvent.HIDE_ALL_BET_LINES));
        }
        //return this._tl;
        return null;
    }

    public getAnimation():TimelineLite | null {
        if(this._gameData.singleWin <= 0) {
            return null;
        }

        this._tl = new TimelineLite({paused:false});
        const noWinAnimations:TimelineLite = this.getNoWinSymbolAnimations();
        const winAnimations:TimelineLite = this.getWinSymbolAnimations();
        this._tl.add([
            noWinAnimations, //this.getNoWinSymbolAnimations(),
            winAnimations, //this.getWinSymbolAnimations(),
            this.getBetlineAnimations()
        ]);
        //this._tl.play(0);
        return this._tl
    }


    public getBetlineAnimations():TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        // #287
        if (!WinterHoardGameConfig.instance.WIN_BELOW_STAKE) {
            tl.add(() => SoundPlayer.playEffect(WinterHoardSoundConfig.WIN_LINE), 0);
        }
        (<IBetLineWinData[]>this._gameData.betWins).forEach((betwin:IBetLineWinData, i:number)=>{
                tl.add(WinterHoardGameModuleConfig.animatedWinLines.playAnimatedWinline(betwin.betLineIndex, betwin.amount.toString()), 0);
        });
        return tl;
    }

    public getNoWinSymbolAnimations():TimelineLite {
        const tl:TimelineLite = new TimelineLite();

        //tl.add(() => {}, 0.1);
        if(this._gameData.mode === GameMode.NORMAL) {
            for (let reelId = 0; reelId < 5; reelId++) {
                const reelCoords: [number, number][] = this._symbolStates.getCoordinatesWithStateInReel(reelId, GameConfig.instance.SYMBOL_STATES.noWin!.keyword);
                for (let coords of reelCoords) {
                    if(!(this._gameData!.nextMode.indexOf("FREESPIN") > -1 && Reels.getSymbol(coords[0], coords[1])!.symName === "S")){
                        tl.add(this.getSymbolAnimation(coords[0], coords[1], GameConfig.instance.SYMBOL_STATES.noWin!), 0.1);
                    }
                }

                tl.add(this.getSymbolAnimation(reelId, -1, GameConfig.instance.SYMBOL_STATES.noWin!), 0.1);
                tl.add(this.getSymbolAnimation(reelId, 4, GameConfig.instance.SYMBOL_STATES.noWin!), 0.1);
            }
        }


        return tl;
    }


    public getWinSymbolAnimations():TimelineLite {
        const tl:TimelineLite = new TimelineLite();

        //tl.add(() => {}, 0.1);
        for(let reelId = 0; reelId < 5; reelId++) {
            const reelCoords:[number, number][] = this._symbolStates.getCoordinatesWithStateInReel(reelId, GameConfig.instance.SYMBOL_STATES.win!.keyword);
            const reelTL:TimelineLite = new TimelineLite();
            for(let coords of reelCoords) {
                reelTL.add(this.getSymbolAnimation(coords[0], coords[1], GameConfig.instance.SYMBOL_STATES.win), 0.1);
            }
            tl.add(reelTL, reelId * 0.1);
        }

        return tl;
    }

    private getSymbolAnimation(reelId:number, symbolId:number, state:ISymbolStateConfig):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        const symbol:StateReelSymbol | null = Reels.getSymbol(reelId, symbolId);
        if(symbol) {
            const symbolAnimation:Timeline = symbol.changeState({
                state : state,
                //setStack : true,
                fadeInDuration : 0.0
            })!;

            tl.add([
                () => this.sortSymbolDepthToTop(symbol),
                symbolAnimation
            ]);
        }
        return tl;
    }


    protected sortSymbolDepthToTop(symbol:StateReelSymbol):void {
        if(symbol && symbol.parent) {
            symbol.parent.addChild(symbol);
        }
    }


}